import * as Sentry from '@sentry/react';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import { CssBaseline, Stack } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DragEventHandler, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from '@tanstack/react-router';
import router from '@/utils/router.ts';
import useDateFnsLocale from './hooks/useDateFnsLocale.ts';
import ColorModeProvider from './providers/ColorModeProvider.tsx';
import ThemeProvider from './providers/ThemeProvider.tsx';
import ApolloClientProvider from './providers/ApolloClientProvider.tsx';
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback.tsx';
import RTLCacheProvider from './providers/RTLCacheProvider.tsx';

export default function App() {
  const {
    t,
    i18n: { resolvedLanguage, languages, dir },
  } = useTranslation(['manager']);
  const adapterLocale = useDateFnsLocale();

  const handleDragEvent = useCallback<DragEventHandler<HTMLDivElement>>(
    (event) => event.preventDefault(),
    [],
  );

  useEffect(() => {
    const lng = resolvedLanguage ?? languages[0];
    document.documentElement.setAttribute('lang', lng);
    document.documentElement.setAttribute('dir', dir(lng));
  }, [resolvedLanguage, languages, dir]);

  return (
    <RTLCacheProvider>
      <ColorModeProvider>
        <ThemeProvider>
          <CssBaseline />
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={adapterLocale}>
            <Sentry.ErrorBoundary fallback={ErrorBoundaryFallback} showDialog>
              <SnackbarProvider
                maxSnack={5}
                autoHideDuration={5000}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                <ApolloClientProvider>
                  <NuqsAdapter>
                    <Stack
                      minHeight="100vh"
                      onDragOver={handleDragEvent}
                      onDrop={handleDragEvent}>
                      <meta
                        name="description"
                        content={t('manager:app.description')}
                      />
                      <RouterProvider router={router} />
                    </Stack>
                  </NuqsAdapter>
                </ApolloClientProvider>
              </SnackbarProvider>
            </Sentry.ErrorBoundary>
          </LocalizationProvider>
        </ThemeProvider>
      </ColorModeProvider>
    </RTLCacheProvider>
  );
}
